import { Link as RouterLink } from "react-router-dom";
import BusinessOutlined from "@material-ui/icons/BusinessOutlined";
import SearchIcon from "@material-ui/icons/Search";
import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import InputAdornment from "@material-ui/core/InputAdornment";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";

import MainContainer from "../../components/MainContainer";
import { PageHeader, StatusBadge, EmptyState } from "../../components/ui";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import CompanyModal from "../../components/CompaniesModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useDate } from "../../hooks/useDate";
import moment from "moment";
import { canManageTenants } from "../../utils/tenant";
import { TenantTypeBadge } from "../../components/CompaniesManager";
import { usePanelStyles } from "../../components/QueueIntegrationModal/panelKit";
import { getTokens } from "../../styles/tokens";

const reducer = (state, action) => {
  if (action.type === "LOAD_COMPANIES") {
    const companies = action.payload;
    const newCompanies = [];

    companies.forEach((company) => {
      const companyIndex = state.findIndex((u) => u.id === company.id);
      if (companyIndex !== -1) {
        state[companyIndex] = company;
      } else {
        newCompanies.push(company);
      }
    });

    return [...state, ...newCompanies];
  }

  if (action.type === "UPDATE_COMPANIES") {
    const company = action.payload;
    const companyIndex = state.findIndex((u) => u.id === company.id);

    if (companyIndex !== -1) {
      state[companyIndex] = company;
      return [...state];
    } else {
      return [company, ...state];
    }
  }

  if (action.type === "DELETE_COMPANIES") {
    const companyId = action.payload;

    const companyIndex = state.findIndex((u) => u.id === companyId);
    if (companyIndex !== -1) {
      state.splice(companyIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    // vencimento próximo / vencida: tinta suave na linha + data no tom do aviso
    rowWarn: { backgroundColor: t.status.warn.bg },
    rowDown: { backgroundColor: t.status.down.bg },
    dueWarn: { color: t.status.warn.fg, fontWeight: 600 },
    dueDown: { color: t.status.down.fg, fontWeight: 600 },
    sub: { display: "block", fontSize: 12, color: t.textSecondary, fontWeight: 400 },
    nowrap: { whiteSpace: "nowrap" },
    emptyCell: { padding: 0, borderBottom: "none" },
  };
});

const Companies = () => {
  const classes = useStyles();
  const panel = usePanelStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [deletingCompany, setDeletingCompany] = useState(null);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [companies, dispatch] = useReducer(reducer, []);
  const { dateToClient, datetimeToClient } = useDate();

  const { user } = useContext(AuthContext);

  useEffect(() => {
    // Revenda: super ou admin de revendedor. Usa o `user` reativo do contexto —
    // só decide depois que o usuário estiver carregado (id presente), para não
    // redirecionar por causa de um snapshot vazio no primeiro render.
    if (!user || !user.id) return;
    if (canManageTenants(user)) return;
    toast.error(
      "Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando."
    );
    const timer = setTimeout(() => {
      history.push(`/`);
    }, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchCompanies = async () => {
        try {
          const { data } = await api.get("/companiesPlan/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_COMPANIES", payload: data.companies });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchCompanies();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  const handleOpenCompanyModal = () => {
    setSelectedCompany(null);
    setCompanyModalOpen(true);
  };

  const handleCloseCompanyModal = () => {
    setSelectedCompany(null);
    setCompanyModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditCompany = (company) => {
    setSelectedCompany(company);
    setCompanyModalOpen(true);
  };

  const handleDeleteCompany = async (companyId) => {
    try {
      await api.delete(`/companies/${companyId}`);
      toast.success(i18n.t("compaies.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingCompany(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const renderPlanValue = (row) => {
    return row.planId !== null
      ? row.plan.amount
        ? row.plan.amount.toLocaleString("pt-br", { minimumFractionDigits: 2 })
        : "00.00"
      : "-";
  };

  // "warn" = vence em até 5 dias; "down" = vencida; null = em dia
  const dueTone = (record) => {
    if (moment(record.dueDate).isValid()) {
      const diff = moment(record.dueDate).diff(moment(), "days");
      if (diff >= 1 && diff <= 5) return "warn";
      if (diff <= 0) return "down";
    }
    return null;
  };

  const formatFolderSize = (size) => {
    if (!size || size === 0) return '0 Bytes';
    
    const units = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    let index = 0;
    let formattedSize = size;

    while (formattedSize >= 1024 && index < units.length - 1) {
        formattedSize /= 1024;
        index++;
    }

    return `${formattedSize.toFixed(2)} ${units[index]}`;
};

  return (
    <MainContainer maxWidth>
      <ConfirmationModal
        title={
          deletingCompany &&
          `${i18n.t("compaies.confirmationModal.deleteTitle")} ${
            deletingCompany.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteCompany(deletingCompany.id)}
      >
        {i18n.t("compaies.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <CompanyModal
        open={companyModalOpen}
        onClose={handleCloseCompanyModal}
        aria-labelledby="form-dialog-title"
        companyId={selectedCompany && selectedCompany.id}
      />
      <PageHeader
        title={i18n.t("compaies.title")}
        subtitle="Consulte planos, situação e uso das empresas sob sua administração."
        badge={<StatusBadge color="neutral" dot={false} label={`${companies.length}`} />}
        actions={
          <Button
            component={RouterLink}
            to="/settings?tab=companies"
            variant="contained"
            color="primary"
            startIcon={<BusinessOutlined />}
          >
            Gerenciar empresas
          </Button>
        }
      />
      <div className={panel.toolbar}>
        <TextField
          className={panel.toolbarGrow}
          type="search"
          size="small"
          variant="outlined"
          value={searchParam}
          onChange={(event) => setSearchParam(event.target.value)}
          placeholder="Buscar empresa por nome…"
          inputProps={{ "aria-label": "Buscar empresa por nome" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </div>

      <Paper className={panel.tableCard} elevation={0} onScroll={handleScroll}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="right">{i18n.t("compaies.table.ID")}</TableCell>
              <TableCell>{i18n.t("compaies.table.name")}</TableCell>
              <TableCell>{i18n.t("compaies.table.type")}</TableCell>
              <TableCell>{i18n.t("compaies.table.status")}</TableCell>
              <TableCell>{i18n.t("compaies.table.namePlan")}</TableCell>
              <TableCell align="right">{i18n.t("compaies.table.value")}</TableCell>
              <TableCell>{i18n.t("compaies.table.createdAt")}</TableCell>
              <TableCell>{i18n.t("compaies.table.dueDate")}</TableCell>
              <TableCell>{i18n.t("compaies.table.lastLogin")}</TableCell>
              <TableCell align="right">{i18n.t("compaies.table.folderSize")}</TableCell>
              <TableCell align="right">{i18n.t("compaies.table.totalFiles")}</TableCell>
              <TableCell>{i18n.t("compaies.table.lastUpdate")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {companies.map((company) => {
                const tone = dueTone(company);
                return (
                  <TableRow
                    key={company.id}
                    className={
                      tone === "warn" ? classes.rowWarn : tone === "down" ? classes.rowDown : undefined
                    }
                  >
                    <TableCell align="right" className={panel.cellMuted}>{company.id}</TableCell>
                    <TableCell>
                      <span className={panel.cellStrong}>{company.name}</span>
                      <span className={classes.sub}>{company.email}</span>
                    </TableCell>
                    <TableCell><TenantTypeBadge type={company.type} /></TableCell>
                    <TableCell>
                      <StatusBadge
                        size="sm"
                        status={company.status === false ? "inactive" : "active"}
                        label={
                          company.status === false
                            ? i18n.t("compaies.table.no")
                            : i18n.t("compaies.table.yes")
                        }
                      />
                    </TableCell>
                    <TableCell className={classes.nowrap}>{company?.plan?.name}</TableCell>
                    <TableCell align="right" className={classes.nowrap}>
                      R$ {renderPlanValue(company)}
                    </TableCell>
                    <TableCell className={classes.nowrap}>
                      {dateToClient(company.createdAt)}
                    </TableCell>
                    <TableCell className={classes.nowrap}>
                      <span
                        className={
                          tone === "warn" ? classes.dueWarn : tone === "down" ? classes.dueDown : undefined
                        }
                      >
                        {dateToClient(company.dueDate)}
                      </span>
                      <span className={classes.sub}>{company.recurrence}</span>
                    </TableCell>
                    <TableCell className={classes.nowrap}>
                      {datetimeToClient(company.lastLogin)}
                    </TableCell>
                    <TableCell align="right" className={classes.nowrap}>
                      {formatFolderSize(company?.metrics?.folderSize || 0)}
                    </TableCell>
                    <TableCell align="right">
                      {company?.metrics?.numberOfFiles || 0}
                    </TableCell>
                    <TableCell className={classes.nowrap}>
                      {datetimeToClient(company?.metrics?.lastUpdate || 'Não disponível')}
                    </TableCell>
                  </TableRow>
                );
              })}
              {loading && <TableRowSkeleton columns={12} />}
              {!loading && companies.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} className={classes.emptyCell}>
                    <EmptyState
                      icon={<BusinessOutlined />}
                      title={
                        searchParam
                          ? "Nenhuma empresa encontrada"
                          : i18n.t("compaies.table.emptyTitle")
                      }
                      message={
                        searchParam
                          ? "Confira o nome digitado ou limpe a busca."
                          : i18n.t("compaies.table.emptyMessage")
                      }
                    />
                  </TableCell>
                </TableRow>
              )}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Companies;
